import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'terminal' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => {
    return (
      <button
        className={cn(
          'inline-flex items-center justify-center rounded-app text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-surface)]',
          'disabled:pointer-events-none disabled:opacity-50',
          {
            'bg-gradient-to-b from-primary-500 to-primary-600 text-white shadow-app-sm hover:from-primary-600 hover:to-primary-700': variant === 'default',
            'bg-rose-600 text-white hover:bg-rose-700': variant === 'destructive',
            'bg-[var(--app-action-terminal)] text-[var(--app-action-terminal-foreground)] hover:bg-[var(--app-action-terminal-hover)]':
              variant === 'terminal',
            'border border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text-muted)] hover:bg-[var(--app-surface-soft)] hover:text-[var(--app-text)]': variant === 'outline',
            'bg-[var(--app-surface-strong)] text-[var(--app-text)] hover:bg-[var(--app-border)]': variant === 'secondary',
            'text-[var(--app-text-muted)] hover:bg-[var(--app-surface-soft)] hover:text-[var(--app-text)]': variant === 'ghost',
            'text-primary-700 underline-offset-4 hover:underline': variant === 'link',
            'h-10 px-4 py-2': size === 'default',
            'h-9 rounded-app px-3': size === 'sm',
            'h-11 rounded-app px-8': size === 'lg',
            'h-10 w-10': size === 'icon',
          },
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button };
